import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Handshake, FileText, Video, Phone, Mail, UserRound, ArrowRight, Upload, Calendar, MessageSquare, Clock,
} from "lucide-react";
import { REF_STATUS_HEX, pretty, hexA, fmtDate } from "./referralConstants";

const StatCard = ({ icon: Icon, label, value, to, nav, testid }) => (
  <button onClick={() => to && nav(to)} data-testid={testid}
    className="text-left rounded-xl bg-white border border-[#E6EDF2] p-4 hover:shadow-sm hover:border-[#1C4358]/40 transition-all">
    <span className="h-9 w-9 rounded-lg bg-[#1C4358]/10 text-[#1C4358] flex items-center justify-center"><Icon size={17} /></span>
    <div className="text-2xl font-bold text-[#252221] mt-2 tabular-nums">{value}</div>
    <div className="text-[13px] text-muted-foreground">{label}</div>
  </button>
);

export default function ExternalDashboard() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/external/dashboard").then(({ data }) => setD(data)).catch(() => {}); }, []);

  if (!d) return <div className="space-y-4"><Skeleton className="h-40" /><Skeleton className="h-28" /></div>;
  const s = d.stats || {};
  const contact = d.staff_contact || d.relationship_manager;
  const at = d.account_type;
  const refLabel = d.meta?.referrals_label || "Referrals";

  return (
    <div className="space-y-6" data-testid="external-dashboard">
      <Card className="p-6 sm:p-8 border-0 text-white" style={{ background: "linear-gradient(135deg,#1C4358 0%,#0F2A38 100%)" }}>
        <div className="text-[12px] uppercase tracking-wide font-bold text-white/70" data-testid="external-dashboard-title">{d.meta?.dashboard_title}</div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold mt-1">{d.organization}</h1>
        <p className="text-[15px] text-white/85 mt-2 max-w-xl">{d.welcome}</p>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Handshake} label={`Open ${refLabel}`} value={s.referrals_open ?? 0} to="/external/referrals" nav={nav} testid="external-stat-referrals" />
        <StatCard icon={FileText} label={at === "vendor" ? "Estimates requested" : "Documents"} value={at === "vendor" ? (s.documents_requested ?? 0) : (s.documents_shared ?? 0)} to="/external/documents" nav={nav} testid="external-stat-documents" />
        <StatCard icon={Upload} label={at === "professional" ? "Docs for review" : "Quotes uploaded"} value={s.quotes_uploaded ?? 0} to="/external/documents" nav={nav} testid="external-stat-quotes" />
        <StatCard icon={Video} label="Upcoming meetings" value={s.meetings_upcoming ?? 0} to="/external/meetings" nav={nav} testid="external-stat-meetings" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* Recent referrals */}
          <Card className="p-5 border-[#E6EDF2]" data-testid="external-recent-referrals">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[13px] font-semibold text-[#252221] flex items-center gap-1.5"><Handshake size={15} /> Recent {refLabel.toLowerCase()}</div>
              <button onClick={() => nav("/external/referrals")} className="text-[12px] text-[#9C1E22] font-medium flex items-center gap-1">View all <ArrowRight size={13} /></button>
            </div>
            {(d.recent_referrals || []).length === 0 ? <div className="text-[13px] text-muted-foreground">No referrals yet.</div> : (
              <div className="space-y-2">
                {d.recent_referrals.map((r) => {
                  const hex = REF_STATUS_HEX[r.status] || "#6B7280";
                  return (
                    <button key={r.id} onClick={() => nav(`/external/referrals/${r.id}`)} data-testid={`external-dash-ref-${r.id}`}
                      className="w-full text-left rounded-lg border border-[#EEF3F6] p-3 hover:border-[#1C4358]/40 transition-colors">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: hexA(hex, 0.14), color: hex }}>{r.status_label}</span>
                        <span className="text-[12px] font-semibold text-[#1C4358]">{pretty(r.referral_type)}</span>
                        {r.client_name && <span className="text-[11px] text-[#9AA5B1]">· {r.client_name}</span>}
                      </div>
                      {r.summary && <p className="text-[13px] text-[#252221] mt-1 line-clamp-1">{r.summary}</p>}
                      {r.next_step && <div className="text-[11px] text-[#9C1E22] mt-0.5">Next: {r.next_step}</div>}
                    </button>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Recent messages */}
          {(d.recent_messages || []).length > 0 && (
            <Card className="p-5 border-[#E6EDF2]" data-testid="external-recent-messages">
              <div className="text-[13px] font-semibold text-[#252221] mb-3 flex items-center gap-1.5"><MessageSquare size={15} /> Messages from MovingMyMama</div>
              <div className="space-y-2">
                {d.recent_messages.map((m, i) => (
                  <button key={i} onClick={() => nav(`/external/referrals/${m.referral_id}`)} className="w-full text-left rounded-lg bg-[#F1F5F8] px-3 py-2">
                    <div className="text-[11px] text-[#9AA5B1] mb-0.5">{m.by_name} · {fmtDate(m.at)}</div>
                    <div className="text-[13px] text-[#252221] line-clamp-2">{m.body}</div>
                  </button>
                ))}
              </div>
            </Card>
          )}
        </div>

        <div className="space-y-5">
          {/* Status breakdown */}
          {Object.keys(d.status_counts || {}).length > 0 && (
            <Card className="p-5 border-[#E6EDF2]" data-testid="external-status-counts">
              <div className="text-[13px] font-semibold text-[#252221] mb-2 flex items-center gap-1.5"><Clock size={15} /> Referral statuses</div>
              <div className="space-y-1.5">
                {Object.entries(d.status_counts).map(([label, n]) => (
                  <div key={label} className="flex items-center justify-between text-[13px]">
                    <span className="text-[#4B5563]">{label}</span>
                    <span className="font-bold text-[#1C4358] tabular-nums">{n}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Upcoming meetings */}
          {(d.upcoming_meetings || []).length > 0 && (
            <Card className="p-5 border-[#E6EDF2]" data-testid="external-upcoming-meetings">
              <div className="text-[13px] font-semibold text-[#252221] mb-2 flex items-center gap-1.5"><Calendar size={15} /> Upcoming meetings</div>
              <div className="space-y-2">
                {d.upcoming_meetings.map((m) => (
                  <button key={m.id} onClick={() => nav(`/external/meetings/${m.id}`)} className="w-full text-left rounded-lg border border-[#EEF3F6] p-2.5 hover:border-[#1C4358]/40">
                    <div className="text-[13px] font-medium text-[#252221] truncate">{m.title}</div>
                    <div className="text-[11px] text-[#9AA5B1]">{fmtDate(m.scheduled_at)}</div>
                  </button>
                ))}
              </div>
            </Card>
          )}

          {contact && (
            <Card className="p-5 border-[#E6EDF2]" data-testid="external-contact-card">
              <div className="text-[11px] uppercase tracking-wide text-[#1C4358]/60 font-bold flex items-center gap-1.5"><UserRound size={13} /> Your MovingMyMama contact</div>
              <div className="font-display font-semibold text-[#252221] mt-1.5">{contact.full_name}</div>
              <div className="flex flex-col gap-1.5 mt-2 text-[14px] text-[#4B5563]">
                {contact.email && <a href={`mailto:${contact.email}`} className="flex items-center gap-1.5 hover:text-[#1C4358]"><Mail size={14} /> {contact.email}</a>}
                {contact.phone && <a href={`tel:${contact.phone}`} className="flex items-center gap-1.5 hover:text-[#1C4358]"><Phone size={14} /> {contact.phone}</a>}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
